import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { clamp, isEmpty, isUndefined } from 'lodash';
import { Plane, Vector3 } from 'three';
import { truncateToPoint2D } from '../../../cross-team-shared/misc-utils';
import { assertStore } from '../../../cross-team-shared/misc-utils/assert-store';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import {
  CUBE_MIN_LEGAL_SCALE,
  CuboidLocationState3D,
} from '../../state/cuboid-location-state-3d';
import { CuboidState3D } from '../../state/cuboid-state-3d';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { angleBetween } from '../../toolbars/measure-toolbar/angle-utils';
import { KdTreeService } from '../../utils/kd-tree.service';
import { MouseCoordinateService } from '../../utils/mouse-coordinate.service';
import { pointToVector, vectorToPoint } from '../../utils/point-cloud-utils';
import {
  directionBetween,
  distanceBetween,
  midPoint,
} from '../../utils/point-utils';
import { RayCastService } from '../../utils/ray-cast.service';
import { AbstractShapeToolHelper } from './abstract-shape-tool-helper';
import { CuboidTool3D } from './cuboid-tool-3d';

export const cuboidLengthToHeightRatio = 0.35;
export const largeTruckMaxHeight = 5;
export const searchAreaRatio = 1.4;
// keep the in between size close to the average vehicle size
export const vehicleWidthToLengthRatio = 0.39;
export const vehicleHeightToLengthRatio = 0.35;

export class ArrowCuboidToolHelper extends AbstractShapeToolHelper<CuboidTool3D> {
  private mouseCoordinateService: MouseCoordinateService;
  private kdTreeService: KdTreeService;
  private rayCastService: RayCastService;

  private inProgressArrowCuboidStore = this.store('render')(
    'inProgressArrowCuboid',
  );
  private rayCastingPlane = new Plane();

  constructor(
    tool: CuboidTool3D,
    injector: Injector,
    inProgressCuboidStore: Store<CuboidState3D>,
  ) {
    super(tool, inProgressCuboidStore, injector);

    this.mouseCoordinateService = injector.get(MouseCoordinateService);
    this.kdTreeService = injector.get(KdTreeService);
    this.rayCastService = injector.get(RayCastService);
  }

  override startListening(): void {
    const initialClickPoint =
      this.mouseCoordinateService.getGroundedMousePosition();

    this.rayCastingPlane.setFromNormalAndCoplanarPoint(
      new Vector3(0, 0, 1),
      pointToVector(initialClickPoint),
    );

    this.inProgressArrowCuboidStore.set({
      // copying the arrays since the store modifies the values by reference
      reference: [...initialClickPoint],
      target: [...initialClickPoint],
    });

    super.startListening();
    this.listenToContainerPoint();
  }

  handleClick() {
    if (!this.isValidCuboid()) {
      return;
    }

    // Ground the cuboid position instead of grounding the final click point,
    // because on extreme slopes the final cuboid length will change
    const location = this.inProgressCuboidStore.state().key_locations[0];
    const groundZ = this.kdTreeService.getGroundZ(
      truncateToPoint2D(location.position),
    );

    // compute the height based of the in-progress grounded cuboid
    const height = this.getHeightToFitPoints({
      ...location,
      position: getGroundedPosition(location, groundZ),
    });

    this.inProgressCuboidStore('key_locations')(0).setUsing((locationState) => {
      const scale: Point3D = [location.scale[0], location.scale[1], height];

      return {
        ...locationState,
        position: getGroundedPosition({ ...locationState, scale }, groundZ),
        scale,
      };
    });

    void this.stopListening();
  }

  protected override cleanUp() {
    this.inProgressArrowCuboidStore.set(undefined);
    super.cleanUp();
  }

  protected override buildKeyLocation(state: PointCloudContentState) {
    const inProgressArrowCuboidState = state.render.inProgressArrowCuboid;
    assertDefined(inProgressArrowCuboidState);

    const { reference, target } = inProgressArrowCuboidState;
    const location = buildCuboidLocationFromMiddleLine(
      reference,
      target,
      state.render.frameNumber,
    );

    return location;
  }

  private updateInProgressCuboid() {
    const keyLocation = this.buildKeyLocation(this.store.state());
    this.inProgressCuboidStore('key_locations').set([keyLocation]);
  }

  private listenToContainerPoint() {
    this.subscribeTo(
      this.store('mouse')('containerPoint').$,
      (containerPoint) => {
        const inProgressArrowStore = this.store('render')(
          'inProgressArrowCuboid',
        );
        assertStore(
          inProgressArrowStore,
          'inProgressArrowCuboid should be defined',
        );

        const groundedImagePoint =
          this.rayCastService.getPlaneIntersectionPoint(
            this.rayCastingPlane,
            containerPoint,
          );
        this.store.batch(() => {
          inProgressArrowStore('target').set(vectorToPoint(groundedImagePoint));
          this.updateInProgressCuboid();
        });
      },
    );
  }

  private isValidCuboid() {
    const inProgressCuboidStore = this.store('render')('inProgressCuboid');
    assertStore(inProgressCuboidStore, 'inProgressCuboid should be defined');

    const { scale } = inProgressCuboidStore.state().key_locations[0];

    return scale.every((axis) => axis >= CUBE_MIN_LEGAL_SCALE);
  }

  private getHeightToFitPoints(location: CuboidLocationState3D) {
    const originalHeight = location.scale[2];
    const closestPoints = this.kdTreeService.getPointsWithinLocation(
      {
        position: location.position,
        scale: [
          location.scale[0],
          location.scale[1],
          originalHeight * searchAreaRatio,
        ],
        rotation: location.rotation,
      },
      (a, b) => a.position.z - b.position.z,
    );

    if (isUndefined(closestPoints) || isEmpty(closestPoints)) {
      return originalHeight;
    }

    const highestZ = getBestZCeilingGuess(
      closestPoints.map((p) => vectorToPoint(p.position)),
      0.2,
    );

    const currentGround = location.position[2] - originalHeight / 2;
    const newHeight = Math.abs(highestZ - currentGround);
    const minimumAllowedHeight = 1.2;

    return Math.max(newHeight, minimumAllowedHeight);
  }
}

function getGroundedPosition(
  { position, scale }: Pick<CuboidLocationState3D, 'position' | 'scale'>,
  groundZ: number,
): Point3D {
  const height = scale[2];
  return [position[0], position[1], groundZ + height / 2];
}

function buildCuboidLocationFromMiddleLine(
  reference: Point3D,
  target: Point3D,
  frameNumber: number,
): CuboidLocationState3D {
  const length = distanceBetween(target, reference);
  const scale = getLengthBasedScale(length);

  const position = midPoint([reference, target]);

  const lineDirection = directionBetween(reference, target);
  const foiDirection: Point2D = [0, -1];
  let yaw = angleBetween(foiDirection, truncateToPoint2D(lineDirection));
  yaw = isNaN(yaw) ? 0 : yaw;

  return {
    visibility: 1,
    points: [],
    position: getGroundedPosition({ position, scale }, position[2]),
    rotation: [0, 0, yaw],
    scale,
    frame_number: frameNumber,
  };
}

function getLengthBasedScale(length: number) {
  const vruMinWidth = 0.5;
  const largeTruckMaxWidth = 4;
  const width = clamp(
    length * vehicleWidthToLengthRatio,
    vruMinWidth,
    largeTruckMaxWidth,
  );

  const vruMinHeight = 1.35;
  const height = clamp(
    length * vehicleHeightToLengthRatio,
    vruMinHeight,
    largeTruckMaxHeight,
  );

  const scale: Point3D = [width, length, height];
  return scale;
}

function getBestZCeilingGuess(points: Point3D[], toleranceMeters: number) {
  const percentile90thIndex = Math.floor(points.length * (90 / 100));
  const closestPoints90thPortion = points.slice(percentile90thIndex);

  const zPercentile90th = closestPoints90thPortion[0][2];
  const standardZDeviation = standardDeviation(
    closestPoints90thPortion.map((p) => p[2]),
    zPercentile90th,
  );

  let highestZ: number;
  if (standardZDeviation < toleranceMeters) {
    highestZ = closestPoints90thPortion.slice(-1)[0][2];
  } else {
    highestZ = zPercentile90th;
  }

  return highestZ;
}

function standardDeviation(
  points: number[],
  mean: number = points.reduce((sum, point) => sum + point, 0) / points.length,
) {
  const variance = points.reduce(
    (sum, point) => sum + Math.pow(point - mean, 2),
    0,
  );
  return Math.sqrt(variance / points.length);
}
